import { ref, watch, computed } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { LocalStorage } from 'quasar'
import { defineStore } from 'pinia'
import { useApi } from 'src/boot/axios'
import countries from 'src/assets/countries.json'

interface Account {
  id?: number
  first_name?: string | null
  last_name?: string | null
  email_address?: string | null
  phone_number?: string | null
}

interface AccountUpdatePayload {
  first_name?: string
  last_name?: string
  email_address?: string
  phone_number?: string
}

export enum LogoutReason {
  REFRESH_TOKEN_EXPIRED = 'REFRESH_TOKEN_EXPIRED',
  REFRESH_TOKEN_MALFORMED = 'REFRESH_TOKEN_MALFORMED',
}

function myYeplyLocationFromStoredCountry(): RouteLocationRaw | null {
  const code = LocalStorage.getItem<string>('country_code')
  if (!code || !countries.some(({ code: c }) => c === code)) {
    return null
  }
  return { name: 'my-yeply', params: { countryCode: code } }
}

export const useAccountStore = defineStore('account', () => {
  const router = useRouter()
  const api = useApi()

  const accessToken = ref<string | null>(LocalStorage.getItem('accessToken'))

  watch(accessToken, () => {
    if (accessToken.value) {
      console.debug('Access token changed. Saving to local storage...')
      LocalStorage.set('accessToken', accessToken.value)
    } else {
      console.debug('Access token got unset. Removing from local storage...')
      LocalStorage.remove('accessToken')
    }
  })

  const refreshToken = ref<string | null>(LocalStorage.getItem('refreshToken'))
  const logoutTimer = ref<number>()
  const isLoggingOut = ref(false)
  watch(
    [refreshToken, api],
    async () => {
      if (!api.value) return
      if (refreshToken.value) {
        console.debug('Refresh token changed. Saving to local storage...')
        LocalStorage.set('refreshToken', refreshToken.value)
        setLogoutTimer()
        await getAccount()
      } else {
        console.debug('Refresh token got unset. Removing from local storage...')
        LocalStorage.remove('refreshToken')
        await logout()
      }
    },
    { immediate: true },
  )

  const isLoggedIn = computed(() => !!accessToken.value && !!refreshToken.value)
  const isRefreshingAccessToken = ref(false)
  const accessTokenRefreshCallbacks = ref<(() => void)[]>([])

  const account = ref<Account | null>(LocalStorage.getItem('account'))
  watch(account, () => {
    if (account.value) {
      console.debug('Account changed. Saving to local storage...')
      LocalStorage.set('account', account.value)
    } else {
      console.debug('Account got unset. Removing from local storage...')
      LocalStorage.remove('account')
    }
  })

  async function login(email: string, password: string, redirectTo?: RouteLocationRaw | null) {
    const resolvedRedirect = redirectTo === undefined ? myYeplyLocationFromStoredCountry() : redirectTo
    console.debug('Logging in with redirect to:', resolvedRedirect)
    let responseData
    try {
      if (!api.value) throw new Error('API not initialized')
      responseData = await api.value.post('auth/token/', {
        json: { email, password },
        context: { skipJWTRefresh: true },
      }).json<any>()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        error.code = 'login.error.INVALID_CREDENTIALS'
        error.message = 'Invalid email or password'
      } else {
        error.code = 'login.error.UNKNOWN'
        error.message = `Login failed: ${error.message}`
      }

      throw error
    }

    accessToken.value = responseData.access
    refreshToken.value = responseData.refresh

    await getAccount()

    if (resolvedRedirect) {
      console.debug('Redirecting to:', resolvedRedirect)
      await router.push(resolvedRedirect)
    }
  }

  function setLogoutTimer() {
    try {
      const refreshTokenExpiresAt = JSON.parse(atob(refreshToken.value!.split('.')[1] || '')).exp
      const refreshTokenExpiresIn = refreshTokenExpiresAt * 1000 - Date.now()

      clearTimeout(logoutTimer.value)
      console.debug('Setting logout timer with delay of', refreshTokenExpiresIn, 'ms')
      logoutTimer.value = window.setTimeout(() => {
        void logout(LogoutReason.REFRESH_TOKEN_EXPIRED)
      }, refreshTokenExpiresIn)
    } catch {
      console.warn('Refresh token is malformed. Logging out...')
      void logout(LogoutReason.REFRESH_TOKEN_MALFORMED)
    }
  }

  async function logout(reason?: LogoutReason, redirectPathAfterLogin?: string) {
    console.debug('Logging out with reason:', reason)
    if (isLoggingOut.value) {
      console.debug('Aborting logout because another logout is already in progress.')
      return
    }

    isLoggingOut.value = true
    console.debug('Clearing tokens...')
    accessToken.value = null
    refreshToken.value = null

    if (router.currentRoute.value.name === 'login') {
      console.debug('Already on login page. Not redirecting.')
      await router.replace({ name: 'login', query: { reason, next: router.currentRoute.value.query.next } })
    } else if (router.currentRoute.value.name === 'my-yeply') {
      console.debug('Redirecting to login page with reason', reason)
      await router.push({
        name: 'login',
        query: { reason, next: redirectPathAfterLogin || router.currentRoute.value.fullPath },
      })
    } else {
      console.debug('Not redirecting to login page because current route is not my-yeply', reason)
    }

    isLoggingOut.value = false
  }

  function requestAccessTokenRefresh() {
    console.debug('Requesting access token refresh')
    if (isRefreshingAccessToken.value) {
      console.debug('Access token is already being refreshed. Waiting for refresh to finish...')
      return new Promise((resolve) => accessTokenRefreshCallbacks.value.push(() => resolve(true)))
    }

    console.debug('Access token is not being refreshed. Refreshing now...')
    return refreshAccessToken()
  }

  async function refreshAccessToken() {
    console.debug('Refreshing access token...')
    isRefreshingAccessToken.value = true

    try {
      if (!api.value) throw new Error('API not initialized')
      const responseData = await api.value.post('auth/token/refresh/', {
        json: { refresh: refreshToken.value },
        context: { skipJWTRefresh: true },
      }).json<any>()
      accessToken.value = responseData.access
      console.debug('Received new access token')
      console.debug('Calling access token refresh callbacks...')
      accessTokenRefreshCallbacks.value.forEach((callback) => callback())
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Refresh token most likely expired. Logging out...')
        await logout(LogoutReason.REFRESH_TOKEN_EXPIRED)
      } else {
        error.message = `Failed refreshing access token: ${error.message}`
        throw error
      }
    } finally {
      accessTokenRefreshCallbacks.value = []
      isRefreshingAccessToken.value = false
    }
  }

  async function getAccount() {
    let responseData
    try {
      if (!api.value) throw new Error('API not initialized')
      responseData = await api.value.get('my-yeply/account').json<any>()
    } catch (error: any) {
      error.message = `Failed loading account: ${error.message}`
      throw error
    }

    account.value = {
      id: responseData.id,
      first_name: responseData.first_name,
      last_name: responseData.last_name,
      email_address: responseData.email,
      phone_number: responseData.phone_number,
    }
    if (!account.value) return
  }

  async function updateAccount(payload: AccountUpdatePayload) {
    if (!account.value) throw Error('Account not loaded')

    let responseData
    try {
      if (!api.value) throw new Error('API not initialized')
      responseData = await api.value.patch(`users/${account.value.id}/`, { json: payload }).json<any>()
    } catch (error: any) {
      error.message = `Failed updating account: ${error.message}`
      throw error
    }

    account.value = responseData
  }

  function useUpdatableAccountProp<K extends keyof AccountUpdatePayload>(prop: K) {
    return computed({
      get() {
        return account.value?.[prop]
      },
      set(value: AccountUpdatePayload[K]) {
        account.value = { ...account.value, [prop]: value }
      },
    })
  }

  async function requestPasswordReset(email: string, redirectPath: string) {
    try {
      if (!api.value) throw new Error('API not initialized')
      await api.value.post('auth/password/request-reset', {
        json: {
          email,
          password_reset_url: window.location.origin + redirectPath,
        },
      })
    } catch (error: any) {
      error.message = `Failed sending password reset link: ${error.message}`
      throw error
    }
  }

  async function verifyPasswordResetToken(uid: string, token: string) {
    try {
      if (!api.value) throw new Error('API not initialized')
      await api.value.post('auth/password/validate-reset-token', { json: { uid, token } })
      return true
    } catch {
      return false
    }
  }

  async function resetPassword(uid: string, token: string, newPassword: string) {
    try {
      if (!api.value) throw new Error('API not initialized')
      await api.value.post('auth/password/reset', { json: { uid, token, new_password: newPassword } })
    } catch (error: any) {
      error.message = `Failed resetting password: ${error.message}`
      throw error
    }
  }

  return {
    accessToken,
    isLoggedIn,
    account,

    login,
    logout,
    requestAccessTokenRefresh,
    getAccount,
    updateAccount,
    useUpdatableAccountProp,
    requestPasswordReset,
    verifyPasswordResetToken,
    resetPassword,
  }
})
